'use client';

import { ReactNode, useEffect } from 'react';
import Link from 'next/link';
import { useAuth } from '@/components/AuthProvider';
import { isProUser, hasUsedTrial, getTrialDaysRemaining } from '@/lib/auth';
import { track } from '@/lib/analytics';

// ── Icons ─────────────────────────────────────────────────────────────────────

function LockIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="12" width="18" height="14" rx="3" />
      <path d="M9 12V8a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1.5 7L5.5 11L12.5 3" />
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

interface PremiumGateProps {
  feature: string;
  children?: ReactNode;
}

function getFeatureOutcomeCopy(feature: string): { title: string; body: string } {
  const lowerFeature = feature.toLowerCase();

  if (lowerFeature.includes('journal')) {
    return {
      title: 'Keep a complete garden record',
      body: 'Save every observation, photo, and harvest note so your season stays easy to follow.',
    };
  }

  if (lowerFeature.includes('bed') || lowerFeature.includes('plan')) {
    return {
      title: 'Plan your garden with room to grow',
      body: 'Create more beds and layouts so your planting decisions stay organized before anything goes in the ground.',
    };
  }

  if (lowerFeature.includes('plant')) {
    return {
      title: 'Keep planning as your garden expands',
      body: 'Add more plants to your layouts and get clearer spacing decisions before you plant.',
    };
  }

  return {
    title: 'Grow with confidence',
    body: `Get deeper guidance for ${feature} and practical next steps for your garden.`,
  };
}

export default function PremiumGate({ feature, children }: PremiumGateProps) {
  const { user, loading } = useAuth();
  const outcomeCopy = getFeatureOutcomeCopy(feature);
  const gated = !loading && Boolean(user) && !(user && isProUser(user));

  useEffect(() => {
    if (gated) track('upgrade_prompt_shown', { variant: 'pro_feature_view', params: { feature } });
  }, [gated, feature]);

  // While auth loads, render children without a gate (avoids flash)
  if (loading) {
    return children ? <>{children}</> : null;
  }

  // ── Not logged in ──────────────────────────────────────────────────────────
  if (!user) {
    return (
      <div className="bg-white rounded-2xl border border-sand p-8 text-center space-y-4">
        <div className="text-sand flex justify-center"><LockIcon /></div>
        <div>
          <h3 className="font-heading text-[20px] text-dark-text">
            Sign in to access {feature}
          </h3>
          <p className="font-body text-[14px] text-sage mt-1">
            Create a free account to get started.
          </p>
        </div>
        <Link
          href="/login"
          className="inline-block bg-deep-green text-white font-body font-bold text-[14px]
                     px-6 py-2.5 rounded-xl hover:opacity-90 transition-opacity"
        >
          Sign In
        </Link>
      </div>
    );
  }

  const isPro     = isProUser(user);
  const daysLeft  = getTrialDaysRemaining(user);
  const usedTrial = hasUsedTrial(user);

  // ── Pro or active trial: render children ──────────────────────────────────
  if (isPro) {
    return (
      <>
        {/* Subtle trial countdown banner — only for trialling users */}
        {user.tier !== 'pro' && daysLeft > 0 && (
          <div className="flex items-center justify-between bg-light-sage
                          border border-[#C5D9B8] rounded-xl px-4 py-2.5 mb-4">
            <span className="font-body text-[13px] text-sage">
              Trial: <strong className="text-deep-green">
                {daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining
              </strong>
            </span>
            <Link
              href="/pricing"
              className="font-body text-[12px] font-semibold text-deep-green
                         hover:underline transition-colors"
            >
              View plans
            </Link>
          </div>
        )}
        {children}
      </>
    );
  }

  // ── Free, trial expired ────────────────────────────────────────────────────
  if (usedTrial) {
    return (
      <div className="bg-white rounded-2xl border border-sand p-8 text-center space-y-4">
        <div className="text-sand flex justify-center"><LockIcon /></div>
        <div>
          <p className="font-body text-[13px] font-semibold text-sage">
            Your trial has ended
          </p>
          <h3 className="font-heading text-[20px] text-dark-text mt-1">
            Keep your garden guidance active
          </h3>
          <p className="font-body text-[14px] text-sage mt-1 max-w-sm mx-auto leading-relaxed">
            Choose a Pro plan to keep your notes, plans, and seasonal guidance moving with you.
          </p>
        </div>
        <Link
          href="/pricing"
          className="inline-block bg-deep-green text-white font-body font-bold text-[14px]
                     px-6 py-2.5 rounded-xl hover:opacity-90 transition-opacity"
        >
          View plans
        </Link>
      </div>
    );
  }

  // ── Free, trial not yet used ───────────────────────────────────────────────
  return (
    <div className="bg-white rounded-2xl border border-sand p-8 text-center space-y-4">
      <div className="text-sand flex justify-center"><LockIcon /></div>
      <div>
        <h3 className="font-heading text-[20px] text-dark-text">
          {outcomeCopy.title}
        </h3>
        <p className="font-body text-[14px] text-sage mt-1 max-w-sm mx-auto leading-relaxed">
          {outcomeCopy.body}
        </p>
      </div>

      <div className="flex items-center justify-center gap-3 text-[13px] font-body text-sage">
        <span className="flex items-center gap-1.5 text-deep-green"><CheckIcon /> Know what to do next</span>
        <span className="flex items-center gap-1.5 text-deep-green"><CheckIcon /> Stay organized all season</span>
      </div>

      <div className="flex justify-center pt-1">
        <Link
          href="/pricing"
          className="bg-deep-green text-white font-body font-bold text-[14px] px-6 py-2.5
                     rounded-xl hover:opacity-90 transition-opacity text-center"
        >
          Start your free trial
        </Link>
      </div>
      <p className="font-body text-[12px] text-sage">Free for 14 days, cancel any time</p>
    </div>
  );
}

// ── Compact inline upgrade nudge (for subtle UI contexts) ────────────────────

interface UpgradeNudgeProps {
  title?: string;
  body?: string;
  message?: string;
  cta?: string;
  href?: string;
  className?: string;
}

export function UpgradeNudge({
  title,
  body,
  message,
  cta = 'Start free trial',
  href = '/pricing',
  className = '',
}: UpgradeNudgeProps) {
  const displayBody = body ?? message ?? '';

  return (
    <div className={`flex flex-col gap-3 bg-light-sage border border-[#C5D9B8]
                    sm:flex-row sm:items-center
                    rounded-lg px-4 py-3 ${className}`}>
      <div className="flex flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3">
        <div className="min-w-0 flex-1">
          {title && (
            <p className="font-body text-[13px] font-semibold text-dark-text">
              {title}
            </p>
          )}
          {displayBody && (
            <p className="font-body text-[13px] text-sage leading-snug">
              {displayBody}
            </p>
          )}
        </div>
      </div>
      <Link
        href={href}
        className="font-body text-[12px] font-semibold text-deep-green
                   hover:underline flex-shrink-0 self-start sm:self-auto"
      >
        {cta}
      </Link>
    </div>
  );
}
